import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  TextField,
  Switch,
  CircularProgress,
  Alert,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  PowerSettingsNew as PowerIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import siteStatusService, {
  FeatureFlagState,
  FeatureFlagKey,
} from '../services/siteStatus.service';

const SURFACE_LABELS: Record<string, string> = {
  public: 'صفحات عمومی',
  buyer: 'پنل خریدار',
  seller: 'پنل فروشنده',
  admin: 'ادمین',
};

const FeatureFlagManagement: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [flags, setFlags] = useState<FeatureFlagState[]>([]);
  const [loading, setLoading] = useState(true);
  const [editFlag, setEditFlag] = useState<FeatureFlagState | null>(null);
  const [editMessage, setEditMessage] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await siteStatusService.listFlags();
      setFlags(data);
    } catch (err: any) {
      enqueueSnackbar('خطا در دریافت فلگ‌ها', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  const handleToggle = async (flag: FeatureFlagState) => {
    const newEnabled = !flag.enabled;
    // optimistic update
    setFlags((prev) => prev.map((f) => (f.key === flag.key ? { ...f, enabled: newEnabled } : f)));
    try {
      await siteStatusService.updateFlag(flag.key, { enabled: newEnabled });
      enqueueSnackbar(
        newEnabled
          ? `«${flag.nameFa}» فعال شد`
          : `«${flag.nameFa}» غیرفعال شد — کاربران دیگر در سایت آن را نمی‌بینند`,
        { variant: newEnabled ? 'success' : 'warning' }
      );
    } catch (err: any) {
      // revert
      setFlags((prev) => prev.map((f) => (f.key === flag.key ? { ...f, enabled: !newEnabled } : f)));
      enqueueSnackbar('خطا در تغییر وضعیت فلگ', { variant: 'error' });
    }
  };

  const openEditMessage = (flag: FeatureFlagState) => {
    setEditFlag(flag);
    setEditMessage(flag.disabledMessageFa || '');
  };

  const saveMessage = async () => {
    if (!editFlag) return;
    setSaving(true);
    try {
      const updated = await siteStatusService.updateFlag(editFlag.key, {
        disabledMessageFa: editMessage,
      });
      setFlags((prev) =>
        prev.map((f) =>
          f.key === editFlag.key ? { ...f, disabledMessageFa: updated.disabledMessageFa } : f
        )
      );
      enqueueSnackbar('پیام بروزرسانی شد', { variant: 'success' });
      setEditFlag(null);
    } catch (err: any) {
      enqueueSnackbar('خطا در بروزرسانی پیام', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const refreshCache = async () => {
    try {
      await siteStatusService.refreshFlagCache();
      enqueueSnackbar('کش فلگ‌ها پاک شد', { variant: 'info' });
      load();
    } catch {
      enqueueSnackbar('خطا در پاکسازی کش', { variant: 'error' });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            مدیریت قابلیت‌های سایت (Feature Flags)
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            با خاموش‌کردن هر فیچر، تمام منوها و عملیات مربوط به آن در سایت پنهان می‌شود و کاربر پیام
            «این بخش موقتاً غیرفعال است» را می‌بیند.
          </Typography>
        </Box>
        <Button onClick={refreshCache} startIcon={<RefreshIcon />} variant="outlined" size="small">
          پاکسازی کش
        </Button>
      </Stack>

      <Alert severity="warning" sx={{ mb: 2 }}>
        توجه: خاموش‌کردن یک فیچر (مثلاً «قرعه‌کشی») باعث می‌شود APIهای مربوطه با کد{' '}
        <code>503 FEATURE_DISABLED</code> پاسخ دهند و در فرانت تمام لینک‌ها/منوها مخفی شوند. ادمین‌ها
        همچنان دسترسی دارند.
      </Alert>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {flags.map((flag) => (
            <Grid item xs={12} sm={6} md={4} key={flag.key}>
              <Card
                variant="outlined"
                sx={{
                  borderColor: flag.enabled ? 'success.light' : 'error.light',
                  borderWidth: 2,
                  transition: 'all 0.2s',
                }}
              >
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box flex={1}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="h6" fontWeight={700}>
                          {flag.nameFa}
                        </Typography>
                        <Chip
                          icon={<PowerIcon />}
                          size="small"
                          label={flag.enabled ? 'فعال' : 'خاموش'}
                          color={flag.enabled ? 'success' : 'error'}
                        />
                      </Stack>
                      <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                        <code>{flag.key}</code>
                      </Typography>
                    </Box>
                    <Switch
                      checked={flag.enabled}
                      onChange={() => handleToggle(flag)}
                      color="success"
                    />
                  </Stack>

                  <Typography variant="body2" color="text.secondary" mt={1}>
                    {flag.descriptionFa}
                  </Typography>

                  <Divider sx={{ my: 1.5 }} />

                  <Stack direction="row" spacing={0.5} flexWrap="wrap" mb={1}>
                    {flag.surfaces.map((s) => (
                      <Chip
                        key={s}
                        label={SURFACE_LABELS[s] || s}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Stack>

                  {!flag.enabled && flag.disabledMessageFa && (
                    <Alert severity="info" sx={{ mt: 1, fontSize: '0.85rem' }}>
                      پیام به کاربر: {flag.disabledMessageFa}
                    </Alert>
                  )}

                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => openEditMessage(flag)}
                    sx={{ mt: 1 }}
                  >
                    ویرایش پیام
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={!!editFlag} onClose={() => setEditFlag(null)} maxWidth="sm" fullWidth>
        <DialogTitle>ویرایش پیام برای «{editFlag?.nameFa}»</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            پیامی که هنگام خاموش بودن این فیچر به کاربر نمایش داده می‌شود:
          </Typography>
          <TextField
            value={editMessage}
            onChange={(e) => setEditMessage(e.target.value)}
            fullWidth
            multiline
            rows={3}
            inputProps={{ maxLength: 500 }}
            placeholder="مثلاً: این بخش به دلیل بازنگری موقتاً غیرفعال است."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditFlag(null)}>انصراف</Button>
          <Button onClick={saveMessage} variant="contained" disabled={saving}>
            {saving ? 'در حال ذخیره...' : 'ذخیره'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FeatureFlagManagement;
